import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Outlet,
} from "react-router-dom";

import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import PourquoiNous from "@/pages/PourquoiNous";
import Faq from "@/pages/Faq";
import Realisations from "@/pages/Realisations";
import { MentionsLegales, Confidentialite, PlanDuSite } from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CookieBanner from "@/components/CookieBanner";
import PartnersStrip from "@/components/PartnersStrip";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

/**
 * H2Reveal — révèle progressivement chaque H2 du <main> quand il entre à l'écran.
 * Fade + léger slide up (10px, 0.5s). Une seule fois par H2.
 * Réobserve les H2 à chaque changement de route.
 */
function H2Reveal() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Délai court pour laisser le DOM se rendre après le changement de route
    const t = setTimeout(() => {
      const headings = document.querySelectorAll("main h2");
      if (!headings.length) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
      );
      headings.forEach((h) => {
        if (h.classList.contains("is-visible")) return;
        h.classList.add("nx-h2-reveal");
        // Si déjà dans le viewport (au chargement), on révèle immédiatement
        const rect = h.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.9) {
          requestAnimationFrame(() => h.classList.add("is-visible"));
        } else {
          io.observe(h);
        }
      });
      return () => io.disconnect();
    }, 40);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

/**
 * FirstVisitIntro — ajoute une classe .nx-first-visit sur <body> uniquement
 * lors du tout premier chargement de la session (séquence d'entrée Hero).
 */
function FirstVisitIntro() {
  useEffect(() => {
    try {
      if (!sessionStorage.getItem("nx_intro_played")) {
        document.body.classList.add("nx-first-visit");
        sessionStorage.setItem("nx_intro_played", "1");
        // Retire la classe une fois la séquence terminée pour ne pas polluer les rerenders
        const t = setTimeout(() => {
          document.body.classList.remove("nx-first-visit");
        }, 2200);
        return () => clearTimeout(t);
      }
    } catch (_) {
      /* sessionStorage indisponible : on ne joue simplement pas l'intro */
    }
  }, []);
  return null;
}

function PublicLayout() {
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <PartnersStrip />
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <H2Reveal />
          <FirstVisitIntro />
          <CookieBanner />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/pourquoi-nous" element={<PourquoiNous />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/realisations" element={<Realisations />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/confidentialite" element={<Confidentialite />} />
              <Route path="/plan-du-site" element={<PlanDuSite />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
